import { useState, useEffect } from 'react';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import './SMEStyles.css';

const SMESelection = () => {
  const { showToast } = useToast();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [sets, setSets] = useState([]);
  const [selectedSets, setSelectedSets] = useState([]);
  const [selectionReasons, setSelectionReasons] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [aiRecommending, setAiRecommending] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [expandedSet, setExpandedSet] = useState(null);
  const [editingSet, setEditingSet] = useState(null);
  const [editedQuestions, setEditedQuestions] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/sme-selection/pending-requests');
      setRequests(data.requests || []);
    } catch (err) {
      console.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSets = async (request) => {
    try {
      setSelectedRequest(request);
      const { data } = await API.get(`/sme-selection/request/${request.request_id}/sets`);
      setSets(data.sets || []);

      if (request.status !== 'pending_sme_selection' && request.selected_count > 0) {
        const selectedPaperIds = (data.sets || [])
          .filter(set => set.selected_by_sme)
          .map(set => set.paper_id);
        setSelectedSets(selectedPaperIds);
      } else {
        setSelectedSets([]);
      }

      setSelectionReasons({});
      setAiRecommendation(null);
    } catch (err) {
      showToast('Failed to load sets', 'error');
    }
  };

  const handleGetAIRecommendation = async () => {
    try {
      setAiRecommending(true);
      const { data } = await API.post(`/sme-selection/request/${selectedRequest.request_id}/ai-recommend`);
      setAiRecommendation(data.recommendation);
      showToast('AI recommendation generated!', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to get AI recommendation', 'error');
    } finally {
      setAiRecommending(false);
    }
  };

  const handleApplyAIRecommendation = () => {
    if (!aiRecommendation) return;

    const recommendedPaperIds = aiRecommendation.recommended_sets.map(s => s.paper_id);
    setSelectedSets(recommendedPaperIds);

    const reasons = {};
    aiRecommendation.recommended_sets.forEach(s => {
      reasons[s.paper_id] = s.reason;
    });
    setSelectionReasons(reasons);

    showToast('AI recommendations applied!', 'success');
  };

  const handleToggleSet = (paperId) => {
    if (selectedSets.includes(paperId)) {
      setSelectedSets(selectedSets.filter(id => id !== paperId));
    } else {
      if (selectedSets.length >= 3) {
        showToast('You can only select 3 sets', 'warning');
        return;
      }
      setSelectedSets([...selectedSets, paperId]);
    }
  };

  const handleSubmitSelection = async () => {
    if (selectedSets.length !== 3) {
      showToast('Please select exactly 3 sets', 'warning');
      return;
    }

    try {
      setSubmitting(true);

      const selection_reasons = selectedSets.map(paperId =>
        selectionReasons[paperId] || 'Selected by SME'
      );

      await API.post(`/sme-selection/request/${selectedRequest.request_id}/select`, {
        selected_paper_ids: selectedSets,
        selection_reasons,
        ai_assisted: !!aiRecommendation
      });

      showToast('Successfully selected 3 sets! Sent to Moderator.', 'success');
      setSelectedRequest(null);
      setSets([]);
      fetchPendingRequests();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to submit selection', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setSelectedRequest(null);
    setSets([]);
    setSelectedSets([]);
    setSelectionReasons({});
    setAiRecommendation(null);
  };

  const handleEditSet = (set) => {
    setEditingSet(set.paper_id);
    setEditedQuestions({ ...editedQuestions, [set.paper_id]: [...set.questions] });
  };

  const handleCancelEdit = (paperId) => {
    setEditingSet(null);
    const updatedQuestions = { ...editedQuestions };
    delete updatedQuestions[paperId];
    setEditedQuestions(updatedQuestions);
  };

  const handleQuestionChange = (paperId, questionIndex, field, value) => {
    const updated = { ...editedQuestions };
    updated[paperId][questionIndex][field] = value;
    setEditedQuestions(updated);
  };

  const handleSaveEdits = async (paperId) => {
    try {
      setSaving(true);
      const questions = editedQuestions[paperId];

      await API.put(`/sme-selection/paper/${paperId}/questions`, { questions });

      const updatedSets = sets.map(s =>
        s.paper_id === paperId ? { ...s, questions: [...questions] } : s
      );
      setSets(updatedSets);

      setEditingSet(null);
      showToast('Questions updated successfully!', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRejectRequest = async (reason) => {
    if (!selectedRequest) return;

    try {
      setSubmitting(true);
      await API.post(`/sme-selection/request/${selectedRequest.request_id}/reject`, {
        rejection_reason: reason
      });

      showToast('Request rejected successfully. Faculty will be notified.', 'success');
      setSelectedRequest(null);
      setSets([]);
      fetchPendingRequests();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to reject request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (selectedRequest) {
    const isReadOnly = selectedRequest.status !== 'pending_sme_selection';
    const statusConfig = {
      'pending_moderator': { label: 'Sent to Moderator', color: 'info' },
      'pending_hod': { label: 'With HOD', color: 'info' },
      'approved': { label: 'Approved', color: 'success' },
      'rejected': { label: 'Rejected', color: 'danger' }
    };
    const currentStatus = statusConfig[selectedRequest.status];

    return (
      <div className="sme-page">
        <div className="sme-page-header">
          <div>
            <button className="sme-btn sme-btn--secondary" onClick={handleBack} style={{ marginBottom: '0.75rem' }}>
              Back to Requests
            </button>
            <h1>{isReadOnly ? 'View Selected Sets' : 'Select 3 Sets from 10'}</h1>
            <p>{selectedRequest.subject} - {selectedRequest.topic}</p>
            {isReadOnly && currentStatus && (
              <div style={{ marginTop: '0.5rem' }}>
                <span className={`sme-badge sme-badge--${currentStatus.color}`}>
                  Status: {currentStatus.label}
                </span>
              </div>
            )}
          </div>
          {!isReadOnly && (
            <button
              className="sme-btn sme-btn--danger"
              onClick={() => {
                const reason = window.prompt('Please provide a reason for rejecting all 10 sets:');
                if (reason && reason.trim()) {
                  handleRejectRequest(reason);
                }
              }}
            >
              Reject All Sets
            </button>
          )}
        </div>

        {/* AI Recommendation Section */}
        {!isReadOnly && (
          <div className="sme-card">
            <div className="sme-card-header">
              <h2>AI Assistance</h2>
            </div>
            <div className="sme-card-body">
              <div className="sme-actions-end" style={{ justifyContent: 'flex-start' }}>
                <button className="sme-btn sme-btn--primary" onClick={handleGetAIRecommendation} disabled={aiRecommending}>
                  {aiRecommending ? 'Analyzing...' : 'Get AI Recommendation'}
                </button>

                {aiRecommendation && (
                  <>
                    <button className="sme-btn sme-btn--success" onClick={handleApplyAIRecommendation}>
                      Apply AI Recommendations
                    </button>
                    <button
                      className="sme-btn sme-btn--secondary"
                      onClick={() => {
                        setAiRecommendation(null);
                        showToast('AI recommendations cleared', 'info');
                      }}
                    >
                      Clear &amp; Start Over
                    </button>
                  </>
                )}

                {selectedSets.length > 0 && (
                  <button
                    className="sme-btn sme-btn--warning"
                    onClick={() => {
                      setSelectedSets([]);
                      setSelectionReasons({});
                      showToast('Selection cleared. You can select again.', 'info');
                    }}
                  >
                    Clear Selection
                  </button>
                )}
              </div>

              {aiRecommendation && (
                <div className="sme-answer-box" style={{ marginTop: '1rem' }}>
                  <h4 style={{ marginTop: 0 }}>AI Analysis</h4>
                  <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
                    {aiRecommendation.overall_analysis}
                  </p>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {aiRecommendation.recommended_sets.map((rec, idx) => (
                      <div key={idx} style={{ padding: '0.75rem', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                        <strong>#{rec.rank} - Set {rec.set_number}</strong>
                        <p className="sme-muted" style={{ fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                          {rec.reason}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Selection Progress */}
        <div className={`sme-banner ${!isReadOnly && selectedSets.length === 3 ? 'sme-banner--success' : 'sme-banner--info'}`}>
          <span>
            {isReadOnly ? `Selected Sets: ${selectedSets.length}/3` : `Selected: ${selectedSets.length}/3 sets`}
          </span>
          {!isReadOnly && selectedSets.length === 3 && (
            <button className="sme-btn sme-btn--success" onClick={handleSubmitSelection} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Selection'}
            </button>
          )}
        </div>

        {/* Sets Grid */}
        <div className="sme-card-grid">
          {sets.map(set => {
            const isSelected = selectedSets.includes(set.paper_id);
            const isExpanded = expandedSet === set.paper_id;

            return (
              <div
                key={set.paper_id}
                className="sme-card"
                style={{ borderColor: isSelected ? 'var(--success)' : undefined }}
              >
                <div className="sme-card-header" style={isSelected ? { background: 'var(--success-light)' } : undefined}>
                  <div>
                    <h2>{isSelected ? 'Selected — ' : ''}Set {set.set_number}</h2>
                    <p>Quality Score: {set.ai_quality_score} &bull; {set.question_count} questions</p>
                  </div>
                  <div className="sme-actions-end">
                    {!isReadOnly && (
                      <button
                        className={`sme-btn sme-btn--sm ${isSelected ? 'sme-btn--danger' : 'sme-btn--success'}`}
                        onClick={() => handleToggleSet(set.paper_id)}
                        disabled={!isSelected && selectedSets.length >= 3}
                      >
                        {isSelected ? 'Deselect' : 'Select'}
                      </button>
                    )}
                    <button
                      className="sme-btn sme-btn--sm sme-btn--secondary"
                      onClick={() => setExpandedSet(isExpanded ? null : set.paper_id)}
                    >
                      {isExpanded ? 'Hide' : 'View'} Questions
                    </button>
                    {!isReadOnly && isExpanded && editingSet !== set.paper_id && (
                      <button className="sme-btn sme-btn--sm sme-btn--primary" onClick={() => handleEditSet(set)}>
                        Edit Questions
                      </button>
                    )}
                    {editingSet === set.paper_id && (
                      <>
                        <button
                          className="sme-btn sme-btn--sm sme-btn--success"
                          onClick={() => handleSaveEdits(set.paper_id)}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="sme-btn sme-btn--sm sme-btn--secondary"
                          onClick={() => handleCancelEdit(set.paper_id)}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="sme-card-body">
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {(editingSet === set.paper_id ? editedQuestions[set.paper_id] : set.questions).map((q, idx) => (
                        <div key={idx} style={{
                          padding: '1rem',
                          background: editingSet === set.paper_id ? 'var(--warning-light)' : 'var(--bg-secondary)',
                          borderRadius: '8px',
                          border: `1px solid ${editingSet === set.paper_id ? 'var(--warning)' : 'var(--border-color)'}`
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {editingSet === set.paper_id ? (
                              <>
                                <select
                                  className="sme-form-select"
                                  value={q.question_type}
                                  onChange={(e) => handleQuestionChange(set.paper_id, idx, 'question_type', e.target.value)}
                                  style={{ width: 'auto' }}
                                >
                                  <option value="mcq">MCQ</option>
                                  <option value="short_answer">Short Answer</option>
                                  <option value="long_answer">Long Answer</option>
                                </select>
                                <input
                                  type="number"
                                  className="sme-form-input"
                                  value={q.marks}
                                  onChange={(e) => handleQuestionChange(set.paper_id, idx, 'marks', parseInt(e.target.value))}
                                  style={{ width: '90px' }}
                                  min="1"
                                />
                              </>
                            ) : (
                              <>
                                <span className="sme-badge sme-badge--primary">{q.question_type}</span>
                                <span className="sme-badge sme-badge--success">{q.marks} marks</span>
                              </>
                            )}
                          </div>
                          {editingSet === set.paper_id ? (
                            <textarea
                              className="sme-form-textarea"
                              value={q.question_text}
                              onChange={(e) => handleQuestionChange(set.paper_id, idx, 'question_text', e.target.value)}
                              rows="3"
                              style={{ marginBottom: '0.5rem' }}
                            />
                          ) : (
                            <p style={{ margin: '0.5rem 0', fontWeight: '500' }}>
                              Q{idx + 1}. {q.question_text}
                            </p>
                          )}

                          {/* MCQ Options */}
                          {q.question_type === 'mcq' && q.options && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <label className="sme-field-label">Options</label>
                              {editingSet === set.paper_id ? (
                                <div style={{ display: 'grid', gap: '0.5rem' }}>
                                  {q.options.map((opt, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                      <span style={{ fontWeight: 'bold', minWidth: '20px' }}>{String.fromCharCode(65 + i)}.</span>
                                      <input
                                        type="text"
                                        className="sme-form-input"
                                        value={opt}
                                        onChange={(e) => {
                                          const newOptions = [...q.options];
                                          newOptions[i] = e.target.value;
                                          handleQuestionChange(set.paper_id, idx, 'options', newOptions);
                                        }}
                                        style={{ flex: 1 }}
                                      />
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                                        <input
                                          type="radio"
                                          name={`correct-${set.paper_id}-${idx}`}
                                          checked={opt === q.correct_answer}
                                          onChange={() => handleQuestionChange(set.paper_id, idx, 'correct_answer', opt)}
                                        />
                                        <span style={{ fontSize: '0.875rem' }}>Correct</span>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ fontSize: '0.875rem' }}>
                                  {q.options.map((opt, i) => (
                                    <div key={i} style={{
                                      padding: '0.25rem 0.5rem',
                                      background: opt === q.correct_answer ? 'var(--success-light)' : 'transparent',
                                      borderRadius: '6px',
                                      marginBottom: '0.25rem'
                                    }}>
                                      {String.fromCharCode(65 + i)}. {opt}
                                      {opt === q.correct_answer && ' (correct)'}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Answer for Short/Long Answer */}
                          {(q.question_type === 'short_answer' || q.question_type === 'long_answer') && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <label className="sme-field-label">Answer</label>
                              {editingSet === set.paper_id ? (
                                <textarea
                                  className="sme-form-textarea"
                                  value={q.correct_answer || ''}
                                  onChange={(e) => handleQuestionChange(set.paper_id, idx, 'correct_answer', e.target.value)}
                                  rows={q.question_type === 'long_answer' ? 5 : 3}
                                  placeholder="Enter the answer..."
                                />
                              ) : (
                                <div className="sme-answer-box" style={{ whiteSpace: 'pre-wrap' }}>
                                  {q.correct_answer || 'No answer provided'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isSelected && !isReadOnly && (
                  <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)' }}>
                    <label className="sme-form-label">Selection Reason (Optional)</label>
                    <textarea
                      className="sme-form-textarea"
                      value={selectionReasons[set.paper_id] || ''}
                      onChange={(e) => setSelectionReasons({ ...selectionReasons, [set.paper_id]: e.target.value })}
                      placeholder="Why did you select this set?"
                      rows="2"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="sme-page">
      <div className="sme-page-header">
        <div>
          <h1>SME Selection</h1>
          <p>Select 3 best sets from 10 generated sets</p>
        </div>
      </div>

      <div className="sme-card">
        <div className="sme-card-header">
          <h2>All Requests</h2>
        </div>
        {loading ? (
          <div className="sme-spinner-wrap">
            <div className="sme-spinner" />
          </div>
        ) : requests.length === 0 ? (
          <div className="sme-empty">
            <h3>No requests found</h3>
            <p>Generation requests awaiting your selection will appear here.</p>
          </div>
        ) : (
          <div className="sme-table-wrap">
            <table className="sme-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Topic</th>
                  <th>Faculty</th>
                  <th>Sets</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => {
                  const statusConfig = {
                    'pending_sme_selection': { label: 'Pending Selection', color: 'warning' },
                    'pending_moderator': { label: 'With Moderator', color: 'info' },
                    'pending_hod': { label: 'With HOD', color: 'info' },
                    'approved': { label: 'Approved', color: 'success' },
                    'rejected': { label: 'Rejected', color: 'danger' }
                  };
                  const status = statusConfig[req.status] || { label: req.status, color: 'neutral' };

                  return (
                    <tr key={req.request_id}>
                      <td className="sme-td-name">{req.subject}</td>
                      <td>{req.topic}</td>
                      <td className="sme-muted">{req.faculty_name}</td>
                      <td className="sme-muted">
                        {req.selected_count > 0
                          ? `${req.selected_count} selected / ${req.total_sets} total`
                          : `${req.total_sets}/10`}
                      </td>
                      <td>
                        <span className={`sme-badge sme-badge--${status.color}`}>{status.label}</span>
                      </td>
                      <td className="sme-muted">{new Date(req.created_at).toLocaleDateString()}</td>
                      <td>
                        <button className="sme-btn sme-btn--sm sme-btn--primary" onClick={() => handleViewSets(req)}>
                          {req.status === 'pending_sme_selection' ? 'Review & Select' : 'View Sets'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SMESelection;
